import { useState, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { Bot, Mail, ArrowLeft, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';

export default function EmailVerificationPending() {
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("sophie-theme");

    return () => {
      root.classList.remove("sophie-theme");
    };
  }, []);

  const handleResendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setIsResending(true);
    
    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Verification email sent successfully!');
      } else {
        toast.error(data.message || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Resend error:', error);
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-8 card-hover">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-semibold mb-2 text-center">Check your email</h1>
          <p className="text-muted-foreground text-center">
            We've sent a verification link to your email address. Click the link to verify your account.
          </p>
        </div>

        <div className="space-y-6">
          <div className="bg-muted/50 p-4 rounded-lg text-sm space-y-2">
            <p className="font-medium">Next steps:</p>
            <ul className="text-muted-foreground list-disc list-inside space-y-1">
              <li>Check your inbox and spam folder</li>
              <li>Click the verification link</li>
              <li>Sign in to your account</li>
            </ul>
          </div>

          <Button asChild className="w-full h-12 text-base" variant="outline">
            <Link to="/login">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Log In
            </Link>
          </Button>
          
          <div className="border-t pt-6">
            <h4 className="font-medium mb-3 text-center">Didn't receive the email?</h4>
            <form onSubmit={handleResendEmail} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-12" 
                disabled={isResending}
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Resend Verification Email"
                )}
              </Button>
            </form>
          </div>
        </div>
      </Card>
    </div>
  );
}

